import React from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from '@mui/material';
import { FormattedMessage } from 'react-intl';
import { EVENT_TYPE_MAPPER } from 'app/types';
import { ConfirmDialog } from 'app/components';
import { IntlMessage } from 'app/components/Intl';
import { formatDateTime } from 'app/utils/formatter/dateTimeFormatter';
import { useProcessDeclineEventsMutation, useProcessGetErrorsQuery } from 'app/hooks/api/process';
import { useSnakeBar } from 'app/hooks/useSnakeBar';
import { useProcessCustomBackendError } from 'app/hooks/useProcessCustomBackendError';
import { TranslationKeys } from 'app/translations';

type ProcessMessagesDialogProps = {
  open: boolean;
  connectionId: string;
  mongoId: string | null;
  handleClose: () => void;
  onDeclineSuccess?: () => void;
};

const columnCellStyle = (maxWidth: number) => ({
  maxWidth,
  overflowWrap: 'break-word',
  whiteSpace: 'normal',
  verticalAlign: 'middle',
});

const checkboxCellStyle = {
  width: 48,
  padding: 0,
  textAlign: 'center',
  verticalAlign: 'middle',
};

const formatEventType = (eventType: string | null) => {
  if (!eventType) return null;

  const translationKey = EVENT_TYPE_MAPPER[eventType as keyof typeof EVENT_TYPE_MAPPER];

  return translationKey ? <FormattedMessage id={`events.type.${translationKey}`} /> : eventType;
};

const ProcessMessagesDialog = ({
  open,
  connectionId,
  mongoId,
  handleClose,
  onDeclineSuccess,
}: ProcessMessagesDialogProps) => {
  const queryClient = useQueryClient();
  const { showSuccessSnakeBar } = useSnakeBar();
  const processCustomBackendError = useProcessCustomBackendError();

  const [selectedExternalIds, setSelectedExternalIds] = React.useState<string[]>([]);
  const [confirmDeclineOpen, setConfirmDeclineOpen] = React.useState(false);
  const [page, setPage] = React.useState(1);
  const [pageSize, setPageSize] = React.useState(10);

  const { data, isLoading } = useProcessGetErrorsQuery({
    variables: { connectionId, mongoId: mongoId ?? undefined, page, perPage: pageSize },
  });
  const errors = React.useMemo(() => data?.data ?? [], [data]);
  const totalCount = data?.totalCount ?? 0;

  const declineEventsMutation = useProcessDeclineEventsMutation();

  const handlePageChange = React.useCallback((_event: unknown, newPage: number) => {
    setPage(newPage + 1);
  }, []);

  const handleRowsPerPageChange = React.useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setPageSize(parseInt(event.target.value, 10));
    setPage(1);
  }, []);

  const handleToggleError = React.useCallback((externalId: string) => {
    setSelectedExternalIds(previous =>
      previous.includes(externalId) ? previous.filter(id => id !== externalId) : [...previous, externalId],
    );
  }, []);

  const selectableErrors = React.useMemo(() => errors.filter(error => !!error.externalId), [errors]);

  const areAllSelected = React.useMemo(() => {
    if (selectableErrors.length === 0) return false;
    return selectableErrors.every(error => selectedExternalIds.includes(error.externalId as string));
  }, [selectableErrors, selectedExternalIds]);

  const handleSelectAll = React.useCallback(() => {
    setSelectedExternalIds(areAllSelected ? [] : selectableErrors.map(error => error.externalId as string));
  }, [areAllSelected, selectableErrors]);

  const handleDeclineSelectedClick = React.useCallback(() => {
    setConfirmDeclineOpen(true);
  }, []);

  const confirmDeclineSelected = React.useCallback(() => {
    if (!connectionId || !mongoId || selectedExternalIds.length === 0) return;

    const remainingCount = totalCount - selectedExternalIds.length;

    declineEventsMutation.mutate(
      { connectionId, mongoId, externalIds: selectedExternalIds },
      {
        onSuccess: () => {
          showSuccessSnakeBar({ method: 'declineBatchEvents' });
          setSelectedExternalIds([]);
          queryClient.invalidateQueries(['getProcessErrors', connectionId, mongoId]);
          onDeclineSuccess?.();

          if (remainingCount <= 0) {
            handleClose();
          } else {
            setPage(1);
          }
        },
        onError: error => {
          processCustomBackendError(error, 'declineBatchEvents');
        },
      },
    );
  }, [
    connectionId,
    mongoId,
    selectedExternalIds,
    totalCount,
    declineEventsMutation,
    showSuccessSnakeBar,
    queryClient,
    onDeclineSuccess,
    handleClose,
    processCustomBackendError,
  ]);

  return (
    <>
      <Dialog
        open={open}
        onClose={() => handleClose()}
        fullWidth
        maxWidth="md"
        aria-labelledby="process-messages-dialog"
      >
        <DialogTitle id="process-messages-dialog">
          <FormattedMessage id={TranslationKeys.connection_processes_errorMessages_dialogTitle} />
        </DialogTitle>
        <DialogContent>
          {isLoading && <LinearProgress />}
          {!isLoading && errors.length === 0 && (
            <Typography variant="body2">
              <FormattedMessage id={TranslationKeys.connection_processes_errorMessages_noErrors} />
            </Typography>
          )}
          {!isLoading && errors.length > 0 && (
            <Table sx={{ tableLayout: 'fixed' }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={checkboxCellStyle}>
                    <Checkbox
                      color="primary"
                      checked={areAllSelected}
                      indeterminate={selectedExternalIds.length > 0 && !areAllSelected}
                      onChange={handleSelectAll}
                    />
                  </TableCell>
                  <TableCell sx={columnCellStyle(120)}>
                    <FormattedMessage id={TranslationKeys.connection_processes_errorMessages_column_eventType} />
                  </TableCell>
                  <TableCell sx={columnCellStyle(160)}>
                    <FormattedMessage id={TranslationKeys.connection_processes_errorMessages_column_employeeName} />
                  </TableCell>
                  <TableCell sx={columnCellStyle(320)}>
                    <FormattedMessage id={TranslationKeys.connection_processes_errorMessages_column_errorMessage} />
                  </TableCell>
                  <TableCell sx={columnCellStyle(140)}>
                    <FormattedMessage id={TranslationKeys.connection_processes_errorMessages_column_occurredAt} />
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {errors.map(error => (
                  <TableRow hover key={error.id}>
                    <TableCell sx={checkboxCellStyle}>
                      <Checkbox
                        color="primary"
                        disabled={!error.externalId}
                        checked={!!error.externalId && selectedExternalIds.includes(error.externalId)}
                        onChange={() => error.externalId && handleToggleError(error.externalId)}
                      />
                    </TableCell>
                    <TableCell sx={columnCellStyle(120)}>{formatEventType(error.eventType)}</TableCell>
                    <TableCell sx={columnCellStyle(160)}>
                      {error.employeeName}
                      {error.employeeSocialSecurityNumber ? ` (${error.employeeSocialSecurityNumber})` : ''}
                    </TableCell>
                    <TableCell sx={columnCellStyle(320)}>{error.errorMessage}</TableCell>
                    <TableCell sx={columnCellStyle(140)}>
                      {error.occurredAt ? formatDateTime(error.occurredAt) : null}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'space-between' }}>
          {!isLoading && errors.length > 0 ? (
            <TablePagination
              component="div"
              count={totalCount}
              onPageChange={handlePageChange}
              onRowsPerPageChange={handleRowsPerPageChange}
              page={page - 1}
              rowsPerPage={pageSize}
              rowsPerPageOptions={[10, 25, 100]}
            />
          ) : (
            <span />
          )}
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="contained"
              color="primary"
              onClick={handleDeclineSelectedClick}
              disabled={selectedExternalIds.length === 0 || declineEventsMutation.isLoading}
            >
              {' '}
              <FormattedMessage id={TranslationKeys.connection_processes_errorMessages_declineSelected} />
            </Button>
            <Button variant="contained" onClick={() => handleClose()} color="secondary">
              <IntlMessage value={'close'} />
            </Button>
          </Box>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        title={<FormattedMessage id={TranslationKeys.connection_processes_errorMessages_declineConfirmTitle} />}
        open={confirmDeclineOpen}
        setOpen={setConfirmDeclineOpen}
        onConfirm={confirmDeclineSelected}
      >
        <FormattedMessage id={TranslationKeys.connection_processes_errorMessages_declineConfirmMessage} />
      </ConfirmDialog>
    </>
  );
};

export default ProcessMessagesDialog;
